import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import KeyIcon from "@mui/icons-material/Key";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import useStyles from "./styles";
import {
  UploadResult,
  useAbortRun,
  useActiveRun,
  usePoiCredentials,
  usePoiSources,
  usePoiTerritories,
  usePoiUpload,
} from "../../api/poi";

const ACCEPTED = ".csv,.tsv,.json,.jsonl,.ndjson,.xlsx,.xls";
const MAX_MB = 50;

/**
 * Import tab — the "drop a file here" surface plus API-key status.
 *
 * Two panels, in the order an operator actually needs them:
 *   1. File import: works with no credentials at all, so it's first.
 *   2. Credential status: which keys are configured, for the sources that
 *      need one. Read-only by design — see the note rendered in the panel.
 */
export const ImportTab: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { sources } = usePoiSources();
  const { countries, groups, defaultCountry } = usePoiTerritories();
  const { credentials, isLoading: loadingCreds } = usePoiCredentials();

  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [sourceKey, setSourceKey] = useState("file_upload");
  const [country, setCountry] = useState(defaultCountry);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<UploadResult | null>(null);

  const upload = usePoiUpload();
  const abortRun = useAbortRun();

  // Second, parallel request that reads the counters the pipeline flushes
  // every 200 records. The upload POST itself is blocked for the whole run,
  // so it can never report anything past the byte-upload phase.
  //
  // Kept alive on error too: the "already in progress" case resolves the
  // mutation, and the unlock button needs the offending run's id to target.
  const activeRun = useActiveRun(sourceKey, upload.isPending || upload.isError);
  const liveStats = activeRun?.stats ?? {};

  /** Only sources that declare accepts_upload can receive a file. */
  const uploadTargets = useMemo(
    () => sources.filter((s) => s.enabled && s.driver === "file"),
    [sources]
  );

  const countryOptions = useMemo(
    () => [
      ...groups.map((g) => ({ value: g, label: `${g} (groupe)` })),
      ...countries.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` })),
    ],
    [groups, countries]
  );

  const acceptFile = useCallback(
    (picked: File | undefined | null) => {
      if (!picked) return;

      const ext = picked.name.split(".").pop()?.toLowerCase() ?? "";
      const allowed = ["csv", "tsv", "json", "jsonl", "ndjson", "xlsx", "xls"];

      if (!allowed.includes(ext)) {
        // Checked here as well as server-side: catching it before a 50 MB
        // upload beats catching it after.
        setResult(null);
        setFile(null);
        window.alert(
          t("poi.badFormat", `Format non supporté (.${ext}). Accepté : CSV, TSV, JSON, JSONL, XLSX.`)
        );
        return;
      }

      if (picked.size > MAX_MB * 1024 * 1024) {
        window.alert(t("poi.tooBig", `Fichier trop volumineux (max ${MAX_MB} Mo).`));
        return;
      }

      setFile(picked);
      setResult(null);
      setProgress(0);
    },
    [t]
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const run = (dryRun: boolean) => {
    if (!file) return;

    setProgress(0);
    upload.mutate(
      { file, source: sourceKey, country, dryRun, onProgress: setProgress },
      { onSuccess: (data) => setResult(data) }
    );
  };

  const selectedSource = sources.find((s) => s.key === sourceKey);

  return (
    <Box>
      {/* ── 1. File import ─────────────────────────────────────────────── */}
      <Card className={classes.card} sx={{ mb: 3 }}>
        <CardContent className={classes.cardContent}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {t("poi.importFile", "Importer un fichier")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              "poi.importFileIntro",
              "CSV, TSV, JSON, JSONL ou XLSX. Les colonnes sont reconnues automatiquement (name/nom, address/adresse, latitude/lat, phone/tel…). Aucune clé API requise."
            )}
          </Typography>

          {/* Dropzone */}
          <div
            className={`${classes.dropzone} ${dragging ? classes.dropzoneActive : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
          >
            {file ? (
              <>
                <InsertDriveFileIcon sx={{ fontSize: 40, color: "primary.main" }} />
                <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(file.size / 1024 / 1024).toFixed(2)} Mo —{" "}
                  {t("poi.clickToChange", "cliquez pour changer")}
                </Typography>
              </>
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 44, color: "text.disabled" }} />
                <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
                  {t("poi.dropHere", "Glissez un fichier ici")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("poi.orClick", "ou cliquez pour parcourir")} — max {MAX_MB} Mo
                </Typography>
              </>
            )}

            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED}
              hidden
              onChange={(e) => {
                acceptFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </div>

          {/* Options */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
            <FormControl size="small" sx={{ minWidth: 240, flex: "1 1 240px" }}>
              <InputLabel>{t("poi.targetSource", "Enregistrer comme")}</InputLabel>
              <Select
                value={sourceKey}
                label={t("poi.targetSource", "Enregistrer comme")}
                onChange={(e) => setSourceKey(e.target.value)}
              >
                {uploadTargets.map((s) => (
                  <MenuItem key={s.key} value={s.key}>
                    {s.key} — {t("poi.trust", "confiance")} {s.trust}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200, flex: "1 1 200px" }}>
              <InputLabel>{t("poi.country", "Pays")}</InputLabel>
              <Select
                value={country}
                label={t("poi.country", "Pays")}
                onChange={(e) => setCountry(e.target.value)}
              >
                {countryOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedSource && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {t("poi.trustExplain", {
                trust: selectedSource.trust,
                defaultValue: `Niveau de confiance ${selectedSource.trust} : ce fichier pourra remplir un champ vide, mais ne remplacera jamais la valeur d'une source plus fiable.`,
              })}
            </Typography>
          )}

          {upload.isPending && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant={progress < 100 ? "determinate" : "indeterminate"}
                value={progress}
              />
              <Typography variant="caption" color="text.secondary">
                {progress < 100
                  ? t("poi.uploading", `Envoi… ${progress}%`)
                  : t("poi.parsing", "Analyse et import en cours…")}
              </Typography>

              {/* Live server-side counters. Their absence for the first couple
                  of seconds is normal: the run row is only written once the
                  file has finished uploading. */}
              {progress >= 100 && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                  {liveStats.fetched !== undefined ? (
                    <>
                      <Chip size="small" variant="outlined" label={`${t("poi.fetched", "Lus")}: ${liveStats.fetched}`} />
                      <Chip size="small" variant="outlined" label={`${t("poi.mapped", "Traités")}: ${liveStats.mapped ?? 0}`} />
                      {!!liveStats.rejected && (
                        <Chip size="small" color="error" variant="outlined" label={`${t("poi.rejected", "Rejetés")}: ${liveStats.rejected}`} />
                      )}
                      {liveStats.to_project !== undefined && (
                        <Chip
                          size="small"
                          color="info"
                          variant="outlined"
                          label={`${t("poi.projecting", "Écriture")}: ${liveStats.projected ?? 0}/${liveStats.to_project}`}
                        />
                      )}
                    </>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {t("poi.waitingCounters", "En attente des premiers compteurs…")}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/*
            A lock held with no live process behind it is the one failure an
            operator cannot resolve by waiting or retrying, so it gets a button
            rather than just an error string.
          */}
          {upload.isError && /en cours|in progress/i.test(
            (upload.error as any)?.response?.data?.message ?? ""
          ) && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={
                activeRun ? (
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<LockOpenIcon />}
                    disabled={abortRun.isPending}
                    onClick={() => abortRun.mutate(activeRun.id)}
                    sx={{ textTransform: "none" }}
                  >
                    {t("poi.unlock", "Déverrouiller")}
                  </Button>
                ) : undefined
              }
            >
              <AlertTitle>{t("poi.lockedTitle", "Source verrouillée")}</AlertTitle>
              {(upload.error as any)?.response?.data?.message}
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {t(
                  "poi.lockedHelp",
                  "Si l'import précédent a été interrompu (timeout), le verrou se libère seul au bout de 20 minutes — ou immédiatement via le bouton."
                )}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              disabled={!file || upload.isPending}
              onClick={() => run(true)}
              sx={{ textTransform: "none" }}
            >
              {t("poi.preview", "Prévisualiser")}
            </Button>

            <Button
              variant="contained"
              startIcon={
                upload.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
              disabled={!file || upload.isPending}
              onClick={() => run(false)}
              sx={{ textTransform: "none" }}
            >
              {t("poi.import", "Importer")}
            </Button>
          </Box>

          {/* Preview / result */}
          {result?.preview && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>{t("poi.previewTitle", "Prévisualisation (rien n'a été écrit)")}</AlertTitle>
              {t("poi.previewSummary", {
                scanned: result.scanned,
                unmappable: result.unmappable,
                defaultValue: `${result.scanned} ligne(s) analysée(s), ${result.unmappable} illisible(s).`,
              })}

              {!!result.sample?.length && (
                <TableContainer component={Paper} className={classes.tableWrap} sx={{ mt: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("poi.name", "Nom")}</TableCell>
                        <TableCell>{t("poi.kind", "Type")}</TableCell>
                        <TableCell>{t("poi.country", "Pays")}</TableCell>
                        <TableCell>lat</TableCell>
                        <TableCell>lng</TableCell>
                        <TableCell>{t("poi.phone", "Tél.")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.sample.map((row: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell>{row.name ?? "—"}</TableCell>
                          <TableCell>{row.kind}</TableCell>
                          <TableCell>{row.country_code ?? "—"}</TableCell>
                          <TableCell className={classes.monoSmall}>{row.latitude ?? "—"}</TableCell>
                          <TableCell className={classes.monoSmall}>{row.longitude ?? "—"}</TableCell>
                          <TableCell className={classes.monoSmall}>{row.phone_e164 ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Alert>
          )}

          {result && !result.preview && (
            <Alert severity={result.rejected ? "warning" : "success"} sx={{ mt: 2 }}>
              <AlertTitle>{t("poi.importDone", "Import terminé")}</AlertTitle>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                <Chip size="small" label={`${t("poi.fetched", "Lus")}: ${result.fetched ?? 0}`} />
                <Chip size="small" label={`${t("poi.mapped", "Traités")}: ${result.mapped ?? 0}`} />
                <Chip size="small" color="success" label={`${t("poi.signsCreated", "Créés")}: ${result.signs_created ?? 0}`} />
                <Chip size="small" label={`${t("poi.signsUpdated", "Mis à jour")}: ${result.signs_updated ?? 0}`} />
                {!!result.review_queue && (
                  <Chip size="small" color="warning" label={`${t("poi.toReview", "À vérifier")}: ${result.review_queue}`} />
                )}
                {!!result.rejected && (
                  <Chip size="small" color="error" label={`${t("poi.rejected", "Rejetés")}: ${result.rejected}`} />
                )}
              </Box>

              {result.rejections && Object.keys(result.rejections).length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {Object.entries(result.rejections).map(([reason, n]) => (
                    <Typography key={reason} variant="caption" display="block" className={classes.monoSmall}>
                      {reason}: {n}
                    </Typography>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ── 2. Credentials ─────────────────────────────────────────────── */}
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <KeyIcon fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              {t("poi.credentials", "Clés API")}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            {t(
              "poi.credentialsNote",
              "Les clés sont lues depuis le fichier .env du serveur et ne sont jamais affichées ni stockées en base. Cet écran indique seulement si elles sont configurées. Pour en ajouter une, modifiez .env puis redémarrez l'API."
            )}
          </Alert>

          {loadingCreds ? (
            <CircularProgress size={22} />
          ) : credentials.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("poi.noCredentials", "Aucune source configurée ne nécessite de clé.")}
            </Typography>
          ) : (
            <TableContainer component={Paper} className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("poi.source", "Source")}</TableCell>
                    <TableCell>{t("poi.envVar", "Variable .env")}</TableCell>
                    <TableCell>{t("poi.status", "Statut")}</TableCell>
                    <TableCell>{t("poi.value", "Valeur")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {credentials.flatMap((cred) =>
                    cred.variables.map((v, i) => (
                      <TableRow key={`${cred.source}-${v.env_key}`}>
                        <TableCell>
                          {i === 0 && (
                            <>
                              <Typography variant="body2" fontWeight={600}>
                                {cred.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {cred.source}
                              </Typography>
                            </>
                          )}
                        </TableCell>
                        <TableCell className={classes.monoSmall}>{v.env_key}</TableCell>
                        <TableCell>
                          {v.is_set ? (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              icon={<CheckCircleIcon />}
                              label={t("poi.configured", "Configurée")}
                            />
                          ) : (
                            <Chip
                              size="small"
                              color="default"
                              variant="outlined"
                              icon={<ErrorOutlineIcon />}
                              label={t("poi.missing", "Manquante")}
                            />
                          )}
                        </TableCell>
                        <TableCell className={classes.monoSmall}>
                          {v.hint ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            {t(
              "poi.freeSourcesNote",
              "OpenStreetMap et les portails open data (data.economie.gouv.fr, opendata.brussels, points d'accès nationaux AFIR) ne demandent aucune clé : ils se lancent depuis l'onglet Sources."
            )}{" "}
            <Link
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noopener noreferrer"
            >
              ODbL
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
